import React, { useEffect, useState } from 'react';
import AssistantContainer from '@/components/dashbaord/assistant/AssistantContainer.tsx';
import { Box } from '@chakra-ui/react';
import { useColorModeValue } from '@/components/ui/color-mode.tsx';
import { AssistantProvider } from '@/components/dashbaord/assistant/AssistantContext.tsx';
import { useUpdateStatus } from '@/context/UpdateStatusContext';
import { CurrentHouseProvider, useCurrentHouse } from '@/context/CurrentHouseContext';
import KnowledgeBaseBanner from '@/components/dashboard/KnowledgeBaseBanner/KnowledgeBaseBanner';
import { useKnowledgeBaseBannerState } from '@/components/dashboard/KnowledgeBaseBanner/useKnowledgeBaseBannerState';
import DocumentPushPrompt from '@/components/documents/DocumentPushPrompt';
import { useNavigate } from '@tanstack/react-router';
import { propertyNavigateOptions, PropertyRouteTo } from '@/routes';

/**
 * Dashboard content component that uses the current house context
 */
export const DashboardContent: React.FC = () => {
	const { house } = useCurrentHouse();
	const navigate = useNavigate();
	const { isUpdatingKnowledgeBase, knowledgeBaseReadinessState } = useUpdateStatus();
	const [hideKbBannerForChat, setHideKbBannerForChat] = useState(false);
	const kbBanner = useKnowledgeBaseBannerState({ houseId: house.id, housePublicId: house.public_id });

	// Feature flag to block the query UI during updates
	// At the moment this is hardcoded to false
	// but could be made dynamic in the future
	// e.g. via a remote config service or environment variable
	// It seems there is no blocking needed at the moment in the API
	const blockQueryUIFeature = false;

	const overlayBgColor = useColorModeValue('rgba(255, 255, 255, 0.8)', 'rgba(26, 32, 44, 0.8)');

	useEffect(() => {
		setHideKbBannerForChat(false);
	}, [house.id]);

	const showKbBanner = !hideKbBannerForChat && kbBanner.isVisible && knowledgeBaseReadinessState !== null;

	return (
		<>
			<Box
				className="dashboard-container"
				bg="surface.canvas"
				p={0}
				w="full"
				h="100%"
				maxW="container.lg"
				mx="auto"
				position="relative"
			>
				{/* /!* Knowledge Base Update Overlay *!/ */}
				{blockQueryUIFeature && isUpdatingKnowledgeBase && (
					<Box
						position="absolute"
						top="0"
						left="0"
						right="0"
						bottom="0"
						bg={overlayBgColor}
						zIndex="overlay"
						display="flex"
						alignItems="center"
						justifyContent="center"
						flexDirection="column"
						gap={4}
						borderRadius="md"
					></Box>
				)}
				<AssistantProvider houseId={house.id} onQuerySubmitted={() => setHideKbBannerForChat(true)}>
					<DocumentPushPrompt />
					{/* The banner is in an absolute position, to not push down the assistant input */}
					{showKbBanner && (
						<Box
							position="absolute"
							// On mobile, begin over the heading's second line: the greeting remains
							// visible while the banner replaces the question with the active status.
							top={{ base: 'calc(50% - 56px)', md: 16 }}
							left={{ base: 4, md: 0 }}
							right={{ base: 4, md: 0 }}
							maxW="720px"
							mx="auto"
							zIndex={4}
						>
							<KnowledgeBaseBanner
								onDismiss={kbBanner.dismiss}
								onViewRecords={() =>
									void navigate(propertyNavigateOptions(house.public_id, PropertyRouteTo.records, {}))
								}
								progressValue={kbBanner.progressValue}
								state={knowledgeBaseReadinessState}
							/>
						</Box>
					)}
					<AssistantContainer key={house.id} suppressAskReadiness={showKbBanner} />
				</AssistantProvider>
			</Box>
		</>
	);
};

/**
 * Property index page — the main "ask" / dashboard view for a property.
 * Wraps content with CurrentHouseProvider to guarantee a house is selected.
 */
const PropertyIndex: React.FC = () => {
	return (
		<CurrentHouseProvider>
			<DashboardContent />
		</CurrentHouseProvider>
	);
};

export const Route = createFileRoute({
	component: PropertyIndex,
	validateSearch: (search) => ({
		questionId: typeof search.questionId === 'string' ? search.questionId : undefined,
		q: typeof search.q === 'string' ? search.q : undefined,
	}),
});
